import { CaseType, CaseTypeRepository } from '@telespot/domain';
import { CaseDataField } from '@protocols/domain';

export interface SaveCaseTypeInput {
  /** Undefined = create; the repository assigns the id. */
  id?: string;
  name: string;
  description?: string;
  /** The ordered case-data fields (the editor's rich discriminated union). */
  fields: CaseDataField[];
  /** The FULL final set of assigned method-type ids (real ids — duplicates collapsed). */
  methodTypeIds: string[];
}

export interface SaveCaseTypeOutput {
  caseTypeId: string;
}

export enum SaveCaseTypeErrorCode {
  MISSING_NAME = 'A non-blank `name` is required to save a case type.',
  NAME_NOT_UNIQUE = 'Another case type already uses this name.',
  MISSING_PERSISTED_ID = 'CaseTypeRepository.save() returned an empty id.',
}

export class SaveCaseTypeException extends Error {
  constructor(public readonly code: SaveCaseTypeErrorCode) {
    super(code.toString());
    this.name = 'SaveCaseTypeException';
    Object.setPrototypeOf(this, SaveCaseTypeException.prototype);
  }
}

export interface ISaveCaseTypeCommand {
  execute(input: SaveCaseTypeInput): Promise<SaveCaseTypeOutput>;
}

/**
 * Persists the case-type row: its name, description, data fields, and the
 * assigned method-type associations. Method types (and their staged
 * pipelines/labels/resources) are persisted upstream by the save cascade, so
 * `methodTypeIds` reaches this command as real ids only.
 *
 * The data fields go into the entity as-is (`CaseType.fields` IS the
 * `CaseDataField` union); the repository's mapper owns the persisted JSON
 * shape. Name uniqueness is enforced here: a DIFFERENT case type owning the
 * name is a conflict, while the case type being edited keeping its own name is
 * allowed.
 */
export class SaveCaseTypeCommand implements ISaveCaseTypeCommand {
  constructor(private readonly caseTypeRepository: CaseTypeRepository) {}

  public async execute(input: SaveCaseTypeInput): Promise<SaveCaseTypeOutput> {
    const name = input.name.trim();
    if (name === '') {
      throw new SaveCaseTypeException(SaveCaseTypeErrorCode.MISSING_NAME);
    }

    await this.assertNameAvailable(name, input.id);

    // On update, preserve the server/foreign-managed fields the editor doesn't own.
    const existing = input.id ? await this.caseTypeRepository.getById(input.id) : undefined;

    const caseType = new CaseType({
      id: input.id,
      name,
      description: input.description ?? '',
      fields: input.fields,
      methodTypeIds: [...new Set(input.methodTypeIds)],
      createdAt: existing?.createdAt,
      updatedAt: existing?.updatedAt,
      deletedAt: existing?.deletedAt,
    });

    const caseTypeId = await this.caseTypeRepository.save(caseType);
    if (!caseTypeId) {
      throw new SaveCaseTypeException(SaveCaseTypeErrorCode.MISSING_PERSISTED_ID);
    }

    return { caseTypeId };
  }

  /*
   * A name collision with any case type OTHER than the one being edited is a
   * conflict. The case type keeping its own name (same id) matches itself and
   * is fair game.
   */
  private async assertNameAvailable(name: string, id: string | undefined): Promise<void> {
    const matches = await this.caseTypeRepository.findByName(name);
    if (matches.some((match) => match.id !== id)) {
      throw new SaveCaseTypeException(SaveCaseTypeErrorCode.NAME_NOT_UNIQUE);
    }
  }
}
